import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Mail, MailOpen, Building2, Briefcase, RefreshCw } from '../components/CustomIcons';
import EmptyState from '../components/EmptyState';

export default function Messages() {
  const { authFetch } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const loadMessages = () => {
    setLoading(true);
    authFetch('/employer/messages/inbox')
      .then(r => setMessages(r.data || []))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadMessages(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const markRead = async (msg) => {
    if (msg.read) { setSelected(msg); return; }
    try {
      await authFetch(`/employer/messages/${msg.id}/read`, { method: 'PUT' });
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, read: true } : m));
      setSelected({ ...msg, read: true });
    } catch (e) {
      setSelected(msg);
    }
  };

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <div style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="messages-page">
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', margin: 0 }}>
              Messages
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '4px 0 0' }}>
              {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''} from employers` : 'Messages from employers and hiring partners'}
            </p>
          </div>
          <button onClick={loadMessages} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '13px' }}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>Loading messages...</div>
        ) : messages.length === 0 ? (
          <EmptyState
            icon={Mail}
            title="No messages yet"
            subtitle="When employers reach out about job opportunities, their messages will appear here."
            action={{ label: 'Browse Jobs', onClick: () => { window.location.href = '/jobs'; } }}
            testId="messages-empty"
          />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1.5fr' : '1fr', gap: '16px', alignItems: 'start' }}>
            {/* Message List */}
            <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #1A1A1A', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>Inbox ({messages.length})</span>
                {unreadCount > 0 && (
                  <span style={{ background: '#F97316', color: '#111', borderRadius: '12px', padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}>{unreadCount} new</span>
                )}
              </div>
              {messages.map(msg => (
                <div key={msg.id}
                  data-testid={`message-item-${msg.id}`}
                  onClick={() => markRead(msg)}
                  style={{
                    padding: '14px 18px',
                    borderBottom: '1px solid #141414',
                    cursor: 'pointer',
                    background: selected?.id === msg.id ? 'rgba(249,115,22,0.06)' : (!msg.read ? 'rgba(249,115,22,0.03)' : 'transparent'),
                    borderLeft: !msg.read ? '3px solid #F97316' : '3px solid transparent',
                    transition: 'background 0.15s',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: 'rgba(249,115,22,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                      {msg.read ? <MailOpen size={15} color="#F97316" /> : <Mail size={15} color="#F97316" />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                        <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: msg.read ? 500 : 700, fontSize: '13px', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>
                          {msg.sender_company || msg.sender_name}
                        </span>
                        {!msg.read && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#F97316', flexShrink: 0 }} />}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '3px' }}>
                        {msg.subject}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>
                        {new Date(msg.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Message Detail */}
            {selected && (
              <div style={{ background: 'var(--bg-surface)', border: '1px solid #1E1E1E', borderRadius: '14px', overflow: 'hidden' }} data-testid="message-detail">
                <div style={{ padding: '20px 22px', borderBottom: '1px solid #1A1A1A' }}>
                  <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)', margin: '0 0 10px 0' }}>
                    {selected.subject}
                  </h2>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                      <Building2 size={13} color="#F97316" />
                      <span>{selected.sender_company || selected.sender_name}</span>
                    </div>
                    {selected.job_title && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                        <Briefcase size={13} color="#F97316" />
                        <span>{selected.job_title}</span>
                      </div>
                    )}
                    <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>
                      {new Date(selected.created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </span>
                  </div>
                </div>
                <div style={{ padding: '22px', lineHeight: 1.7, fontSize: '14px', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                  {selected.body}
                </div>
                {selected.job_title && (
                  <div style={{ padding: '16px 22px', borderTop: '1px solid #1A1A1A' }}>
                    <a href="/jobs" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#F97316', color: '#111', borderRadius: '8px', padding: '9px 18px', fontSize: '13px', fontWeight: 700, textDecoration: 'none', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      <Briefcase size={13} /> View Related Jobs
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
